import { useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import logo from '../../assets/img/wc-logo-big.png';
import './offlineFallback.css';

// Unlike ErrorFallback (a one-way crash page), this is a self-clearing
// overlay: it mounts unconditionally, renders nothing while online, and
// disappears on its own the instant the browser reports a connection again.
export function OfflineFallback() {
    const isOnline = useOnlineStatus();
    const [checking, setChecking] = useState(false);
    const [stillOffline, setStillOffline] = useState(false);

    if (isOnline) return null;

    const handleRetry = async () => {
        setChecking(true);
        setStillOffline(false);
        try {
            // navigator.onLine can be true on a dead network (e.g. LAN with no
            // WAN), so probe with a real request rather than trusting it alone.
            await fetch(`${window.location.origin}/favicon.ico?_=${Date.now()}`, {
                method: 'HEAD',
                cache: 'no-store',
            });
        } catch (_) {
            setStillOffline(true);
        } finally {
            setChecking(false);
            setTimeout(() => setStillOffline(false), 2000);
        }
    };

    return (
        <div className="offline-fallback" role="alert">
            <div className="offline-fallback__panel">
                <img src={logo} alt="Workforce City" className="offline-fallback__logo" />

                <div className="offline-fallback__illustration" aria-hidden="true">
                    <svg viewBox="0 0 200 170" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="100" cy="88" r="82" fill="url(#offGlow)" />

                        <circle className="offline-fallback__spark offline-fallback__spark--1" cx="32" cy="120" r="4" fill="#76DFD2" />
                        <circle className="offline-fallback__spark offline-fallback__spark--2" cx="174" cy="56" r="3" fill="#00BFA5" />
                        <circle className="offline-fallback__spark offline-fallback__spark--3" cx="54" cy="24" r="2.5" fill="#FF8F52" />

                        <circle className="offline-fallback__ping offline-fallback__ping--1" cx="100" cy="118" r="18" stroke="#00BDA5" strokeWidth="2.5" />
                        <circle className="offline-fallback__ping offline-fallback__ping--2" cx="100" cy="118" r="18" stroke="#00BDA5" strokeWidth="2.5" />

                        <path className="offline-fallback__arc offline-fallback__arc--3" d="M30 50 Q100 4 170 50" stroke="#33475C" strokeWidth="6" strokeLinecap="round" strokeOpacity="0.22" strokeDasharray="1 18" />
                        <path className="offline-fallback__arc offline-fallback__arc--2" d="M52 74 Q100 32 148 74" stroke="#33475C" strokeWidth="8" strokeLinecap="round" strokeOpacity="0.45" strokeDasharray="1 15" />
                        <path className="offline-fallback__arc offline-fallback__arc--1" d="M74 98 Q100 76 126 98" stroke="#33475C" strokeWidth="8" strokeLinecap="round" />
                        <circle cx="100" cy="118" r="9" fill="#33475C" />

                        <defs>
                            <radialGradient id="offGlow" cx="0.5" cy="0.45" r="0.6">
                                <stop offset="0%" stopColor="#E6FBF7" />
                                <stop offset="100%" stopColor="#E6FBF7" stopOpacity="0" />
                            </radialGradient>
                        </defs>
                    </svg>
                </div>

                <p className="offline-fallback__eyebrow">Connection lost</p>
                <h1 className="offline-fallback__title">No internet connection</h1>
                <p className="offline-fallback__subtitle">
                    Check your Wi-Fi or mobile data — we'll pick up right where you left off
                    the instant you're back online.
                </p>

                <div className="offline-fallback__status">
                    <span className="offline-fallback__status-dot" />
                    Waiting to reconnect&hellip;
                </div>

                <button type="button" className="offline-fallback__btn" onClick={handleRetry} disabled={checking}>
                    <ReloadOutlined spin={checking} />
                    {checking ? 'Checking…' : stillOffline ? 'Still offline' : 'Try again'}
                </button>
            </div>
        </div>
    );
}
